import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Alert } from "reactstrap";
import { useParams } from "react-router-dom";
import { uploadDriverLicense, updateDriverLicenseInformation, checkJwtToken } from "helpers/fakebackend_helper";
import CameraController from "./CameraController";
import DriverLicenseFlow from "./DriverLicenseFlow";
import StepCompleteActionButton from "./StepCompleteActionButton";
import UploadSignedDocumentControls from "./UploadSignedDocumentControls";

const OnboardingDriverLicense = () => {
    document.title = "Driver License | CurbDash";

    const { token } = useParams();

    const [authorized, setAuthorized] = useState(null);

    const [phase, setPhase] = useState("choose");
    const [preview, setPreview] = useState("");
    const [rawText, setRawText] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [fields, setFields] = useState({});

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const bg = {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #ffffff, #f0f0f0)",
        paddingTop: 60,
        paddingBottom: 60
    };

    useEffect(() => {
        const run = async () => {
            try {
                await checkJwtToken(token);
                setAuthorized(true);
            } catch (e) {
                setAuthorized(false);
            }
        };
        run();
    }, [token]);

    if (authorized === false) {
        return (
            <div style={bg} className="d-flex justify-content-center">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col md={6}>
                            <img
                                src="/cs-logo-circle.png"
                                alt="Curbstand Logo"
                                style={{ width: 120, marginBottom: 30 }}
                            />
                            <h2 className="fw-semibold mb-3">You are not authorized.</h2>
                            <img
                                src="/cs_horizontal_logo.webp"
                                alt="Curbstand"
                                style={{ height: 40 }}
                            />
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    if (authorized === null) {
        return (
            <div style={bg} className="d-flex justify-content-center">
                <h4>Loading...</h4>
            </div>
        );
    }

    const handleTakePhoto = (img) => {
        setPreview(img);
    };

    const handleRetake = () => {
        setPreview("");
        setRawText("");
        setPhase("camera");
    };

    const handleFileUpload = async (blobOrFile) => {
        try {
            setError("");
            setLoading(true);

            const file =
                blobOrFile instanceof File
                    ? blobOrFile
                    : new File([blobOrFile], "driver_license.jpg", { type: "image/jpeg" });

            const form = new FormData();
            form.append("file", file);

            const r = await uploadDriverLicense(token, form);
            if (r.code !== 1) throw new Error(r.message);

            setRawText(r.data);
            setPhase("verify");
        } catch (err) {
            setError(err.message || "Upload failed");
        }
        setLoading(false);
    };

    const handleSubmitFinal = async (payload) => {
        try {
            setLoading(true);
            setError("");

            const r = await updateDriverLicenseInformation(token, payload.data);
            if (r.code !== 1) throw new Error(r.message);

            setPhase("done");
        } catch (err) {
            setError(err.message || "Save failed");
        }
        setLoading(false);
    };

    if (phase === "done") {
        return (
            <div style={bg} className="d-flex justify-content-center">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col md={7}>
                            <img
                                src="/cs-logo-circle.png"
                                alt="Curbstand Logo"
                                style={{ width: 120, marginBottom: 20 }}
                            />
                            <h2 className="fw-bold">Driver License Submitted</h2>
                            <p className="mt-3">
                                Thank you. Your driver license information has been updated.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div style={bg} className="d-flex justify-content-center">
            <Container>
                <Row className="justify-content-center text-center">
                    <Col md={8}>
                        <img
                            src="/cs-logo-circle.png"
                            alt="Curbstand Logo"
                            style={{ width: 120, marginBottom: 30 }}
                        />

                        {/* STEP 1: How to upload */}
                        {phase === "choose" && (
                            <>
                                <h1 className="fw-semibold mb-4">Upload Your Driver License</h1>

                                <UploadSignedDocumentControls
                                    onUploadFile={handleFileUpload}
                                    onTakePicture={() => setPhase("camera")}
                                />
                            </>
                        )}

                        {/* STEP 2: Camera */}
                        {phase === "camera" && (
                            <CameraController
                                videoRef={videoRef}
                                canvasRef={canvasRef}
                                preview={preview}
                                onPhotoTaken={handleTakePhoto}
                                onUpload={handleFileUpload}
                                onRetake={handleRetake}
                            />
                        )}

                        {/* STEP 3: Verification */}
                        {phase === "verify" && (
                            <DriverLicenseFlow
                                preview={preview}
                                rawText={rawText}
                                email="" // email removed from flow
                                onRetake={handleRetake}
                                onSubmit={handleSubmitFinal}
                                fields={fields}
                                setFields={setFields}
                                loading={loading}
                                error={error}
                            />
                        )}

                        {error && (
                            <Alert color="danger" className="mt-3">
                                {error}
                            </Alert>
                        )}
                    </Col>
                </Row>

                {/* Footer */}
                <Row className="justify-content-center mt-4">
                    <Col className="text-center">
                        <img
                            src="/cs_horizontal_logo.webp"
                            alt="Curbstand"
                            style={{ height: 30 }}
                        />
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default OnboardingDriverLicense;